import type { CSSProperties } from 'react';
import type { Universe } from '@/types/universe';
import type { AddRevealRef } from '@/hooks/useScrollReveal';

interface TestimonialsProps {
  universe: Universe;
  activeTestimonial: number;
  onTestimonialChange: (idx: number) => void;
  addRevealRef: AddRevealRef;
}

const serif = "'DM Serif Display', Georgia, serif";

// Positions / inclinaisons des portraits en arrière-plan (desktop uniquement).
const portraitLayout: { style: CSSProperties; object: string }[] = [
  { style: { left: 0, top: '50%', transform: 'translateY(-50%) rotate(-10deg)' }, object: 'object-center' },
  { style: { left: '100px', top: '50%', transform: 'translateY(-30%) rotate(5deg)' }, object: 'object-left' },
  { style: { right: 0, top: '50%', transform: 'translateY(-50%) rotate(10deg)' }, object: 'object-center' },
  { style: { right: '100px', top: '50%', transform: 'translateY(-30%) rotate(-5deg)' }, object: 'object-right' },
];

export default function Testimonials({
  universe,
  activeTestimonial,
  onTestimonialChange,
  addRevealRef,
}: TestimonialsProps) {
  const current = universe.testimonials[activeTestimonial];

  return (
    <section id="testimonials" className="bg-bg-dark py-24 md:py-32 px-6 md:px-10">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div ref={addRevealRef} className="reveal text-center mb-20">
          <p className="flex items-center justify-center gap-3 text-white/60 text-[11px] font-semibold uppercase tracking-widest mb-4">
            <span className="inline-block w-6 h-px bg-accent" />
            Ils se sont filmés avec DreamerLife
            <span className="inline-block w-6 h-px bg-accent" />
          </p>
          <h2
            className="text-white text-5xl md:text-6xl tracking-tighter leading-none"
            style={{ fontFamily: serif, fontWeight: 400 }}>
            {universe.testimonialTitle}<br />
            <span className="italic opacity-70">{universe.testimonialTitleAccent}</span>
          </h2>
        </div>

        {/* Témoignage + portraits en arrière-plan */}
        <div ref={addRevealRef} className="reveal relative flex items-center justify-center min-h-[520px]">
          {portraitLayout.map((p, i) => {
            const photo = universe.testimonialPhotos[i];
            if (!photo) return null;
            return (
              <div key={i} className="absolute hidden lg:block" style={p.style}>
                <div className="w-36 h-52 rounded-3xl overflow-hidden shadow-xl">
                  <img src={photo.src} alt={photo.alt} className={`w-full h-full object-cover grayscale ${p.object}`} />
                </div>
              </div>
            );
          })}

          {/* Carte témoignage centrale */}
          <div className="relative z-10 w-full max-w-md rounded-3xl p-10 text-center shadow-2xl bg-white">
            <div key={current.avatar} className="w-20 h-20 mx-auto mb-5 rounded-2xl overflow-hidden border-4 border-white shadow-lg">
              <img src={current.avatar} alt={current.avatarAlt} className="w-full h-full object-cover" />
            </div>
            <h4 className="text-primary text-2xl tracking-tight" style={{ fontFamily: serif, fontWeight: 400 }}>
              {current.name}
            </h4>
            <p className="text-muted text-[10px] uppercase tracking-widest mt-1 mb-6">{current.role}</p>
            <p className="text-primary text-lg font-semibold italic mb-4" style={{ fontFamily: serif }}>
              &ldquo;{current.quote}&rdquo;
            </p>
            <p className="text-muted text-sm leading-relaxed font-light">{current.text}</p>
            <div className="mt-6 flex justify-center gap-1">
              {[0, 1, 2, 3, 4].map((s) =>
                <span key={s} className="text-accent text-base">★</span>
              )}
            </div>
          </div>
        </div>

        {/* Pastilles de navigation */}
        <div ref={addRevealRef} className="reveal flex justify-center gap-3 mt-10">
          {universe.testimonials.map((t, i) =>
            <button
              key={i}
              onClick={() => onTestimonialChange(i)}
              aria-label={`Voir le témoignage de ${t.name}`}
              className={`h-2 rounded-full transition-all duration-300 ${
                i === activeTestimonial ? 'w-8 bg-accent' : 'w-2 bg-white/20'
              }`} />
          )}
        </div>
      </div>
    </section>
  );
}